import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { ScanLine, CheckCircle2, Loader2, Info, UserCheck, UserX, LifeBuoy } from "lucide-react";
import toast from "react-hot-toast";

interface ScanResponse {
    message: string;
    type?: "participant" | "guest";
    data: {
        participant?: {
            participant_id: number;
            full_name: string;
            email: string;
            institution: string;
        };
        registration?: {
            registration_id: number;
            period_id: number;
            latest_payment?: {
                status: string;
            };
        };
        guest?: {
            id: number;
            full_name: string;
            email: string | null;
            institution: string | null;
            card_number: string;
            card_category?: {
                name: string;
                color_hex?: string | null;
            } | null;
        };
    };
}

export default function RfidScanPage() {
    const [rfidCode, setRfidCode] = useState("");
    const [scanResult, setScanResult] = useState<ScanResponse | null>(null);
    const [scanError, setScanError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-focus input so scanner always works
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    // Ensure input keeps focus when clicking around (optional but good for kiosk mode)
    const handleContainerClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const scanMutation = useMutation({
        mutationFn: async (code: string) => {
            const response = await axiosClient.post(`/rfid/scan`, {
                rfid_number: code
            });
            return response.data;
        },
        onSuccess: (data: ScanResponse) => {
            toast.success(data.message || "Berhasil scan.");
            setScanResult(data);
            setScanError(null);
            setRfidCode("");
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || "Gagal melakukan scan.";
            toast.error(msg);
            setScanError(msg);
            setScanResult(null);
            setRfidCode("");
        }
    });

    const handleScan = (e: React.FormEvent) => {
        e.preventDefault();
        if (!rfidCode.trim()) return;
        scanMutation.mutate(rfidCode);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0" onClick={handleContainerClick}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <ScanLine className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Cek & Scan RFID</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Pastikan kursor berada di kotak input, lalu tap kartu fisik peserta pada reader.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                {/* Scan Area */}
                <div className="bg-white dark:bg-slate-900 p-10 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center text-center min-h-[450px]">
                    
                    <div className="w-32 h-32 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mb-8 animate-pulse">
                        <ScanLine className="w-16 h-16 text-indigo-500" />
                    </div>
                    
                    <h2 className="text-2xl font-bold mb-6 text-slate-700 dark:text-slate-300">Menunggu Tap Kartu...</h2>
                    
                    <form onSubmit={handleScan} className="w-full max-w-sm">
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="password"
                                value={rfidCode}
                                onChange={(e) => setRfidCode(e.target.value)}
                                placeholder="Arahkan kursor & tap kartu"
                                className="w-full h-14 pl-6 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-2 border-indigo-200 dark:border-indigo-800 rounded-2xl text-center text-lg tracking-widest focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all shadow-inner"
                            />
                            {scanMutation.isPending && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                    <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                                </div>
                            )}
                        </div>
                    </form>
                    <p className="text-sm text-slate-400 mt-6 max-w-xs mx-auto">
                        Alat scanner akan mengetikkan kode secara otomatis ke dalam kotak di atas.
                    </p>
                </div>

                {/* Results Area */}
                <div className="h-full flex flex-col min-h-[450px]">
                    {scanResult && scanResult.type === "guest" && scanResult.data.guest ? (
                        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-8 rounded-3xl border border-indigo-200 dark:border-indigo-800 animate-in slide-in-from-right-4 fade-in duration-300 h-full flex flex-col">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-14 h-14 bg-indigo-100 dark:bg-indigo-800 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-300 shrink-0">
                                    <UserCheck className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-indigo-800 dark:text-indigo-300">Akses Diterima</h3>
                                    <p className="text-indigo-600 dark:text-indigo-400 font-medium">Panitia/Tamu teridentifikasi dengan sukses</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-indigo-100 dark:border-indigo-800/50 flex-1 content-start">
                                <div className="sm:col-span-2 mb-2 pb-4 border-b border-slate-100 dark:border-slate-800">
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Nama Lengkap</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">{scanResult.data.guest.full_name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">No. Kartu</p>
                                    <p className="text-base font-bold text-slate-900 dark:text-white font-mono">{scanResult.data.guest.card_number}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Institusi / Afiliasi</p>
                                    <p className="text-base font-bold text-slate-900 dark:text-white">{scanResult.data.guest.institution || "-"}</p>
                                </div>
                                <div className="sm:col-span-2 mt-2">
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Kategori Kartu</p>
                                    <span
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold border"
                                        style={{
                                            backgroundColor: (scanResult.data.guest.card_category?.color_hex || "#6366F1") + "20",
                                            color: scanResult.data.guest.card_category?.color_hex || "#6366F1",
                                            borderColor: (scanResult.data.guest.card_category?.color_hex || "#6366F1") + "40",
                                        }}
                                    >
                                        <CheckCircle2 className="w-5 h-5" /> {scanResult.data.guest.card_category?.name || "-"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : scanResult && scanResult.data.participant ? (
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-8 rounded-3xl border border-emerald-200 dark:border-emerald-800 animate-in slide-in-from-right-4 fade-in duration-300 h-full flex flex-col">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-800 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-300 shrink-0">
                                    <UserCheck className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-emerald-800 dark:text-emerald-300">Akses Diterima</h3>
                                    <p className="text-emerald-600 dark:text-emerald-400 font-medium">Peserta teridentifikasi dengan sukses</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-emerald-100 dark:border-emerald-800/50 flex-1 content-start">
                                <div className="sm:col-span-2 mb-2 pb-4 border-b border-slate-100 dark:border-slate-800">
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Nama Lengkap</p>
                                    <p className="text-xl font-bold text-slate-900 dark:text-white">{scanResult.data.participant.full_name}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Email</p>
                                    <p className="text-base font-bold text-slate-900 dark:text-white break-all">{scanResult.data.participant.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Institusi / Afiliasi</p>
                                    <p className="text-base font-bold text-slate-900 dark:text-white">{scanResult.data.participant.institution || "-"}</p>
                                </div>
                                {scanResult.data.registration && (
                                    <div className="sm:col-span-2 mt-2">
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Status Pembayaran</p>
                                        <div>
                                            {scanResult.data.registration.latest_payment?.status === "paid" ? (
                                                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400 font-bold border border-emerald-200 dark:border-emerald-800/60">
                                                    <CheckCircle2 className="w-5 h-5" /> Lunas / Terverifikasi
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 font-bold border border-amber-200 dark:border-amber-800/60">
                                                    <Info className="w-5 h-5" /> Menunggu Pembayaran
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : scanError ? (
                        <div className="bg-rose-50 dark:bg-rose-900/20 p-8 rounded-3xl border border-rose-200 dark:border-rose-800 animate-in slide-in-from-right-4 fade-in duration-300 flex flex-col justify-center h-full">
                            <div className="flex flex-col items-center text-center">
                                <div className="w-20 h-20 bg-rose-100 dark:bg-rose-800 rounded-full flex items-center justify-center text-rose-600 dark:text-rose-300 mb-6">
                                    <UserX className="w-10 h-10" />
                                </div>
                                <h3 className="text-2xl font-bold text-rose-800 dark:text-rose-300 mb-2">Akses Ditolak</h3>
                                <p className="text-rose-600 dark:text-rose-400 text-lg max-w-sm">{scanError}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-slate-50 dark:bg-slate-800/30 border border-slate-200 border-dashed dark:border-slate-700 rounded-3xl flex flex-col items-center justify-center text-center h-full p-8 animate-in fade-in duration-300">
                            <div className="w-20 h-20 bg-white dark:bg-slate-800 rounded-full shadow-sm flex items-center justify-center text-slate-300 dark:text-slate-600 mb-6 border border-slate-100 dark:border-slate-700">
                                <UserCheck className="w-10 h-10" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">Belum Ada Data Scan</h3>
                            <p className="text-slate-500 dark:text-slate-400 max-w-sm">
                                Hasil informasi identitas peserta akan muncul di area ini ketika kartu RFID berhasil dipindai oleh sistem.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Support / Help Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-800/50 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-300 shrink-0">
                    <LifeBuoy className="w-8 h-8" />
                </div>
                <div className="flex-1 text-center md:text-left">
                    <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">Butuh Bantuan Teknis?</h3>
                    <p className="text-blue-700/80 dark:text-blue-300/80 text-sm md:text-base">
                        Jika alat scanner tidak merespon atau terjadi kendala saat identifikasi kartu RFID, pastikan kabel USB scanner telah terhubung dengan benar ke perangkat Anda. Untuk bantuan lebih lanjut, silakan hubungi tim IT Support Konaspi XII.
                    </p>
                </div>
                <div className="shrink-0 w-full md:w-auto">
                    <button className="w-full md:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-colors shadow-sm">
                        Hubungi IT Support
                    </button>
                </div>
            </div>
        </div>
    );
}
